import { SaveOutlined } from '@ant-design/icons';
import { Button, Col, Collapse, Form, FormListFieldData, Input, InputNumber, Modal, Row, Select, Space } from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { StatePieceStatus } from '../../../../enums';
import { IAddInvoice, IAddInvoiceFormValues, IInvoice } from '../../../../interfaces/invoices';
import { AppDispatch, RootState } from '../../../../store';
import { toggleAddInvoiceModal, updateFeeInvoices } from '../../../../store/feeSlice/FeeSlice';
import { resetInvoiceCreateStatus } from '../../../../store/invoiceSlice/InvoiceSlice';
import { fetchFeeById, fetchFeeInvoices } from '../../../../store/feeSlice/FeeActions';
import React from 'react';
import { ROW_GUTTER, DECIMAL_SEPARATOR } from '../../../../shared/Constants';
import { fetchCustomersLookupList } from '../../../../store/lookupSlice/LookupActions';
import { IFeeTableRow } from '../../../../interfaces/fees';
import './AddInvoiceModal.less';
import { IDispatchResponse } from '../../../../interfaces/IDispatchResponse';
import { addInvoices } from '../../../../store/invoiceSlice/InvoiceActions';

export interface IAddInvoiceModalProps {
    isOpen: boolean;
    defaultSelectedFees?: IFeeTableRow[] | null;
}

export const AddInvoiceModal = (props: IAddInvoiceModalProps) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch<AppDispatch>();
    const { detailFee } = useSelector((state: RootState) => state.feeSlice);
    const invoiceSlice = useSelector((state: RootState) => state.invoiceSlice);
    const lookupSlice = useSelector((state: RootState) => state.lookupSlice);
    const [isValidFeeId, setIsValidFeeId] = useState(false);
    const [feeIdFieldIsTouched, setFeeIdFieldIsTouched] = useState(false);

    const onCreateInvoice = async (values: { fees: IAddInvoiceFormValues[] }) => {
        const fees = values.fees as IAddInvoice[];
        const body: IAddInvoice[] = [...fees];
        const createdInvoices: IDispatchResponse = await dispatch(addInvoices(body));

        if (createdInvoices.payload) {
            for (let i = 0; i < createdInvoices.payload.length; i++) {
                const createdInvoice = createdInvoices.payload[i];
                const feeInvoices: IDispatchResponse = await dispatch(fetchFeeInvoices(createdInvoice.fee.id));
                dispatch(updateFeeInvoices({ feeId: createdInvoice.fee.id, invoices: feeInvoices.payload as IInvoice[] }));
            }
        }
    };

    const validateFeeId = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(fetchFeeById(parseInt(e.target.value)));

        if (!feeIdFieldIsTouched) {
            setFeeIdFieldIsTouched(true);
        }
    };

    useEffect(() => {
        if (invoiceSlice.detailPage.invoice.createStatus === StatePieceStatus.Success) {
            form.resetFields();
            dispatch(toggleAddInvoiceModal());
            dispatch(resetInvoiceCreateStatus());
        }
    }, [invoiceSlice.detailPage.invoice]);

    useEffect(() => {
        let isValid = detailFee.fee.fetchStatus === StatePieceStatus.Success;
        setIsValidFeeId(isValid);
    }, [detailFee.fee.fetchStatus]);

    useEffect(() => {
        dispatch(fetchCustomersLookupList());
    }, []);

    useEffect(() => {
        form.setFieldsValue({
            fees:
                props.defaultSelectedFees?.map((record) => {
                    return {
                        feeId: record.id,
                        amount: record.amountInEur,
                        reasonOfPayment: record.reasonOfPayment.trim(),
                        contactPerson: null,
                        customerId: null,
                    } as IAddInvoiceFormValues;
                }) || [],
        });
    }, [props.isOpen]);

    return (
        <Form
            form={form}
            name="invoice-modal-add"
            onFinish={onCreateInvoice}
            layout="vertical"
            onValuesChange={(changedValue: any, values) => {
                form.setFieldsValue(values);
            }}
        >
            <Modal
                title={`Create new invoice for Fee ${props.defaultSelectedFees?.map((r) => `#${r.id}`).join(', ')}`}
                centered
                open={props.isOpen}
                width={800}
                onCancel={() => dispatch(toggleAddInvoiceModal())}
                footer={[
                    <React.Fragment key={0}>
                        <Button onClick={() => dispatch(toggleAddInvoiceModal())}>Cancel</Button>
                        <Button
                            htmlType="submit"
                            type="primary"
                            icon={<SaveOutlined />}
                            loading={invoiceSlice.detailPage.invoice.createStatus === StatePieceStatus.IsFetching}
                            onClick={() => form.submit()}
                        >
                            Create and close
                        </Button>
                    </React.Fragment>,
                ]}
            >
                <Form.List name="fees">
                    {(fields: FormListFieldData[]) => (
                        <>
                            <Collapse
                                accordion={true}
                                defaultActiveKey={[0]}
                                items={fields.map((field, index) => {
                                    return {
                                        key: index,
                                        label: (
                                            <>{`Fee #${props.defaultSelectedFees ? props.defaultSelectedFees[index]?.id : undefined}`}</>
                                        ),
                                        collapsible: 'header',
                                        forceRender: true,
                                        children: (
                                            <>
                                                <Row>
                                                    <Col span={24}>
                                                        <Space>
                                                            <label className="group-label">Invoice information</label>
                                                        </Space>
                                                    </Col>
                                                </Row>
                                                <Row gutter={ROW_GUTTER}>
                                                    <Col span={8}>
                                                        <label className="property required">Fee Id</label>
                                                        <Form.Item
                                                            {...field}
                                                            key={field.key}
                                                            name={[field.name, 'feeId']}
                                                            rules={[{ required: true, message: "'Fee Id' is required" }]}
                                                            validateStatus={!isValidFeeId && feeIdFieldIsTouched ? 'error' : undefined}
                                                            help={
                                                                !isValidFeeId && feeIdFieldIsTouched
                                                                    ? "'Invalid 'fee Id' is provided."
                                                                    : undefined
                                                            }
                                                        >
                                                            <Input
                                                                disabled={
                                                                    form &&
                                                                    props.defaultSelectedFees &&
                                                                    props.defaultSelectedFees?.length > 0
                                                                        ? true
                                                                        : false
                                                                }
                                                                placeholder="Enter a fee Id"
                                                                onChange={validateFeeId}
                                                            />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={8}>
                                                        <label className="property required">Customer</label>
                                                        <Form.Item
                                                            {...field}
                                                            key={field.key}
                                                            name={[field.name, 'customerId']}
                                                            rules={[{ required: true, message: "'Customer' is required" }]}
                                                        >
                                                            <Select
                                                                showSearch
                                                                placeholder="Select a customer"
                                                                filterOption={(input, option) =>
                                                                    (option!.children as unknown as string)
                                                                        .toLowerCase()
                                                                        .startsWith(input.toLowerCase())
                                                                }
                                                            >
                                                                {lookupSlice.customers.data
                                                                    .filter((f) => !f.isDeleted)
                                                                    .map((c) => (
                                                                        <Select.Option key={c.key} value={c.key} disabled={c.isDeleted}>
                                                                            {c.value}
                                                                        </Select.Option>
                                                                    ))}
                                                            </Select>
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={8}>
                                                        <label className="property">Amount</label>
                                                        <Form.Item {...field} key={field.key} name={[field.name, 'amount']}>
                                                            <InputNumber decimalSeparator={DECIMAL_SEPARATOR}></InputNumber>
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                                <Row gutter={ROW_GUTTER}>
                                                    <Col span={8}>
                                                        <label className="property">Contact person</label>
                                                        <Form.Item
                                                            {...field}
                                                            key={field.key}
                                                            name={[field.name, 'contactPerson']}
                                                            rules={[{ max: 255 }]}
                                                        >
                                                            <Input />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={8}>
                                                        <label className="property required">Reason of payment</label>
                                                        <Form.Item
                                                            {...field}
                                                            key={field.key}
                                                            name={[field.name, 'reasonOfPayment']}
                                                            rules={[
                                                                { required: true, message: "'Reason of Payment' is required", max: 255 },
                                                            ]}
                                                        >
                                                            <Input />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={8}>&nbsp;</Col>
                                                </Row>
                                            </>
                                        ),
                                    };
                                })}
                            />
                        </>
                    )}
                </Form.List>
            </Modal>
        </Form>
    );
};
